import React from 'react';

import { Editor } from '@tinymce/tinymce-react';
import { useSelector } from 'react-redux';
import { languageStoreSelector } from 'store/Languages';
import { UserSessionSelector } from 'store/UserSession';

import { ROLES } from 'utils/Constants';
import axiosPrivate from 'config/axiosPrivate';
import {
  DownloadOneTextWordType,
  downloadHtmlFromContent,
} from 'components/shared/DownloadFile/DownloadTextWordType';

export const TINY_API = 'kgyse4iueutjt36vd1qbqphc0cvv0oqf124t0g7xo43kkwzu';
export const api_key = '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';
const accessToken = 'XUlnLnewifdxKywK4PoBmDldpRLnr9QwWI1fDgKQZ20=';



export const CustomEditor = ({
  height = '90vh',
  content,
  disabled,
  isReadOnly,
  onChange,
  onInit,
  onFocus,
  t,
  threadId,
  assistantId,
  orderType,
  orderContentType,
  placeholder,
  textId,
  onDownloadWord,
  actualHtmlContent,
  downloadFileName,
  allowConsultantEdit = false,
}) => {
  const { selectedLanguage } = useSelector(languageStoreSelector);
  const { user } = useSelector(UserSessionSelector);
  const [isAiPluginActivated, setIsAiPluginActivated] = React.useState(true);
  const [remainingCredits, setRemainingCredits] = React.useState(null);

  const textIdRef = React.useRef(textId);
  const onDownloadWordRef = React.useRef(onDownloadWord);
  const actualHtmlContentRef = React.useRef(actualHtmlContent);
  const contentRef = React.useRef(content);
  const editorRef = React.useRef(null);
  const downloadFileNameRef = React.useRef(downloadFileName);
  React.useEffect(() => { textIdRef.current = textId; }, [textId]);
  React.useEffect(() => { onDownloadWordRef.current = onDownloadWord; }, [onDownloadWord]);
  React.useEffect(() => { actualHtmlContentRef.current = actualHtmlContent; }, [actualHtmlContent]);
  React.useEffect(() => { contentRef.current = content; }, [content]);
  React.useEffect(() => { downloadFileNameRef.current = downloadFileName; }, [downloadFileName]);

  const handleDownloadWord = () => {
    if (onDownloadWordRef.current) {
      onDownloadWordRef.current();
      return;
    }
    const currentTextId = textIdRef.current;
    if (!currentTextId) {
      console.warn('[Tiny] Impossible de télécharger en Word: textId manquant');
      return;
    }
    DownloadOneTextWordType(currentTextId, user);
  };

  const handleDownloadHtml = () => {
    const htmlContent =
      actualHtmlContentRef.current ||
      contentRef.current ||
      editorRef.current?.getContent() ||
      '';
    downloadHtmlFromContent(htmlContent, downloadFileNameRef.current || 'document');
  };

  React.useEffect(() => {
    if (user?.role === ROLES.CONSULTANT) {
      return undefined;
    }

    let isMounted = true;

    const fetchCompanyQuotas = async () => {
      try {
        const { data } = await axiosPrivate.get('/project/company-quotas');
        if (!isMounted) return;
        setIsAiPluginActivated(data?.assistantEnabled !== false);
        const totalCredits = Number(data?.totalCredits);
        const usedCredits = Number(data?.usedCredits);
        if (Number.isFinite(totalCredits) && Number.isFinite(usedCredits)) {
          setRemainingCredits(totalCredits - usedCredits);
        } else {
          const apiRemainingCredits = Number(data?.remainingCredits);
          setRemainingCredits(Number.isFinite(apiRemainingCredits) ? apiRemainingCredits : null);
        }
      } catch (error) {
        if (isMounted) {
          setIsAiPluginActivated(true);
          setRemainingCredits(null);
        }
      }
    };

    fetchCompanyQuotas();

    return () => {
      isMounted = false;
    };
  }, [user?.role]);

  const isAiFeatureEnabled =
    isAiPluginActivated && !(remainingCredits !== null && remainingCredits <= 0);
  const isConsultantRole = user?.role === ROLES.CONSULTANT && !allowConsultantEdit;
  const editorReadOnly = Boolean(isReadOnly) || isConsultantRole;
  // Keep TinyMCE enabled for consultants so download controls remain usable,
  // but force readonly mode + contenteditable=false below.
  const editorDisabled = editorReadOnly && !isConsultantRole ? true : disabled;

  return (
    <Editor
      key={`tiny-editor-${isAiFeatureEnabled ? 'ai-on' : 'ai-off'}-${editorReadOnly ? 'ro' : 'rw'}`}
      value={content}
      apiKey={TINY_API}
      disabled={editorDisabled}
      onEditorChange={editorReadOnly ? undefined : onChange}
      onInit={onInit}
      init={{
        setup: (editor) => {
          console.log('[Tiny] init editor.id=', editor.id, 'textId=', textIdRef.current);
          editorRef.current = editor;

          const enforceReadOnly = () => {
            if (!editorReadOnly) return;
            try {
              if (editor.mode && typeof editor.mode.set === 'function') {
                editor.mode.set('readonly');
              }
              const body = editor.getBody?.();
              if (body) {
                body.setAttribute('contenteditable', 'false');
              }
            } catch (e) { }
          };

          editor.on('init', enforceReadOnly);
          editor.on('SetContent', () => {
            enforceReadOnly();
            setTimeout(() => {
              editor.fire('focus');
              editor.fire('blur');
            }, 0);
          });

          editor.on('focus', () => {
            if (editorReadOnly) {
              enforceReadOnly();
              return;
            }
            if (onFocus) {
              onFocus();
            }
          });

          editor.ui.registry.addIcon(
            'download-icon',
            '<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M5 20h14a1 1 0 0 0 1-1v-3h-2v2H6v-2H4v3a1 1 0 0 0 1 1zm7-3 5-5h-3V4h-4v8H7l5 5z" /></svg>'
          );

          // Bouton dans la toolbar
          editor.ui.registry.addButton('downloadword', {
            icon: 'download-icon',
            text: t('Télécharger'),
            tooltip: t('Télécharger en Word'),
            onAction: handleDownloadWord,
          });

          editor.ui.registry.addMenuItem('downloadwordmenu', {
            text: t('Télécharger en Word'),
            icon: 'download-icon',
            onAction: handleDownloadWord,
          });

          editor.ui.registry.addMenuItem('downloadhtmlmenu', {
            text: t('Télécharger en HTML'),
            icon: 'download-icon',
            onAction: handleDownloadHtml,
          });

          if (editorReadOnly || isConsultantRole) {
            const STYLE_ID = 'tox-consultant-download-only-style';
            const isDownloadControl = (el) => {
              const label = (
                el.getAttribute('aria-label') ||
                el.getAttribute('title') ||
                el.textContent ||
                ''
              )
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '');
              return label.includes('telecharger') || label.includes('download');
            };

            const markDownloadControls = (container) => {
              container.querySelectorAll('.tox-mbtn').forEach((btn) => {
                if (isDownloadControl(btn)) {
                  btn.classList.add('tox-consultant-download-menu');
                }
              });
              container.querySelectorAll('.tox-tbtn').forEach((btn) => {
                if (isDownloadControl(btn)) {
                  btn.classList.add('tox-consultant-download-btn');
                }
              });
            };

            editor.on('init', () => {
              enforceReadOnly();
              if (!document.getElementById(STYLE_ID)) {
                const style = document.createElement('style');
                style.id = STYLE_ID;
                style.textContent = `
                  .tox-tinymce.tox-consultant-download-only .tox-menubar .tox-mbtn:not(.tox-consultant-download-menu),
                  .tox-tinymce.tox-consultant-download-only .tox-toolbar .tox-tbtn:not(.tox-consultant-download-btn),
                  .tox-tinymce.tox-consultant-download-only .tox-toolbar .tox-split-button,
                  .tox-tinymce.tox-consultant-download-only .tox-toolbar__overflow-button {
                    pointer-events: none !important;
                  }
                `;
                document.head.appendChild(style);
              }

              const container = editor.getContainer();
              container.classList.add('tox-consultant-download-only');
              markDownloadControls(container);
              setTimeout(() => markDownloadControls(container), 150);
            });
          }
        },
        branding: false,
        height,
        readonly: editorReadOnly,
        selector: 'textarea#comments-embedded',
        language: selectedLanguage === 'fr' ? 'fr_FR' : 'en',
        placeholder: placeholder,
        plugins: isAiFeatureEnabled
          ? [
            'ai',
            'advlist',
            'autolink',
            'lists',
            'link',
            'image',
            'charmap',
            'preview',
            'anchor',
            'searchreplace',
            'visualblocks',
            'advcode',
            'fullscreen',
            'insertdatetime',
            'media',
            'table',
            'help',
            'wordcount',
            'link',
            'export',
            'powerpaste',
            'autosave',
            'tinycomments',
          ]
          : [
            'advlist',
            'autolink',
            'lists',
            'link',
            'image',
            'charmap',
            'preview',
            'anchor',
            'searchreplace',
            'visualblocks',
            'advcode',
            'fullscreen',
            'insertdatetime',
            'media',
            'table',
            'help',
            'wordcount',
            'link',
            'export',
            'powerpaste',
            'autosave',
            'tinycomments',
          ],
        toolbar: isAiFeatureEnabled
          ? ' fullscreen | undo redo | restoredraft | aidialog aishortcuts | addcomment showcomments deleteallconversations | fontfamily | fontsize | blocks | bold italic underline strikethrough | backcolor forecolor | link image media table | a11ycheck typography | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | help downloadword | removeformat'
          : 'fullscreen | undo redo | restoredraft | addcomment showcomments deleteallconversations | fontfamily | fontsize | blocks | bold italic underline strikethrough | backcolor forecolor | link image media table | a11ycheck typography | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | help downloadword | removeformat',

        // Ajout d'un menu "Télécharger" dans la barre du haut
        menu: {
          telecharger: {
            title: t('Télécharger'),
            items: 'downloadwordmenu downloadhtmlmenu',
          },
        },
        menubar: 'file edit view insert format tools table help telecharger',

        // Configuration du plugin Comments en mode callback
        tinycomments_mode: 'callback',
        tinycomments_author: user?.name || 'Utilisateur Anonyme',
        tinycomments_author_name: user?.name || 'Utilisateur Anonyme',


        // Configuration pour afficher les commentaires dans la sidebar uniquement
        tinycomments_show_resolved: true,
        tinycomments_show_unresolved: true,
        spellchecker_active: false,
        autosave_ask_before_unload: true,
        autosave_interval: '10s',
        autosave_retention: '30m',
        content_langs: [
          { title: 'English (US)', code: 'en_US' },
          { title: 'English (UK)', code: 'en_UK' },
          { title: 'Spanish', code: 'es' },
          { title: 'French', code: 'fr' },
          { title: 'German', code: 'de' },
          { title: 'Portuguese', code: 'pt' },
        ],

        ai_shortcuts: !isAiFeatureEnabled
          ? []
          : [
            {
              id: 'improveWriting',
              title: t('Régénérer'),
              prompt:
                'Rewrite this content with no spelling mistakes, proper grammar, and with more descriptive language, using best writing practices without losing the original meaning.The response should preserve the words between strong tags, any HTML formatting, links, styles and the language in the context',
              selection: true,
            },
          ],

        ai_request: (request, respondWith) => {
          const productOrderbaseUrl =
            'https://prod.ty-pix.com/chatgpt/assistants/calcualte/message/one-message-thread';
          const queryParams = [`assistantId=${assistantId}`];

          const otherOrderBaseUrl = `https://prod.ty-pix.com/chatgpt/gpt-call/new-generation-front?maxTokens=2000&temperature=0.3`;

          if (threadId) {
            queryParams.push(`threadId=${threadId}`);
          }

          const isProductType =
            orderContentType === 'PRODUCT' ||
            orderContentType === 'LONG_PRODUCT';

          const url = isProductType
            ? `${productOrderbaseUrl}?${queryParams.join('&')}`
            : otherOrderBaseUrl;

          const isImproveWriting = request.query.includes(
            'Rewrite this content with no spelling mistakes, proper grammar, and with more descriptive language, using best writing practices without losing the original meaning.The response should preserve the words between strong tags, any HTML formatting, links, styles and the language in the context'
          );

          // Extract the context from the request
          let context = request.context ? request.context : '';

          if (isImproveWriting && isProductType) {
            // Create a new DOM element to parse the context HTML
            const parser = new DOMParser();
            const doc = parser.parseFromString(context, 'text/html');
            const h2Element = doc.querySelector('h2');

            if (h2Element) {
              // Extracting the outerHTML of the h2 element which includes the element itself and its content
              context = h2Element.outerHTML;
            } else {
              // Fallback or handle the absence of an <h2> element
              console.warn('No <h2> element found in the context');
            }
          }

          // Define the system messages and format them.
          const systemMessages =
            'Answer the question based on the context below.\n' +
            'Remove lines with ``` from the response start and response end.\n' +
            'The response should preserve any HTML formatting, links, and styles and the language in the context.\n\n';

          // Combine the context and the query with the system messages to form the prompt
          const prompt = request.query ? request.query : '';

          let currentPrompt = '';

          if (isImproveWriting && isProductType) {
            currentPrompt =
              prompt + " : " + context
          } else {
            currentPrompt =
              prompt + " : " + context
          }

          const requestBody = {
            accessToken: accessToken,
            prompt: currentPrompt,
          };

          const openAiOptions = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(isProductType ? currentPrompt : requestBody),
          };

          respondWith.string((signal) => {
            openAiOptions.signal = signal;

            return window
              .fetch(url, openAiOptions)
              .then(async (response) => {
                if (response.ok) {
                  if (!isProductType) {
                    const textData = await response.text();
                    return textData.trim();
                  } else {
                    const jsonData = await response.json();
                    const responseId = jsonData.responseId;
                    if (typeof responseId === 'string') {
                      return responseId.trim();
                    } else {
                      throw new Error(
                        'responseId not found or is not a string'
                      );
                    }
                  }
                } else {
                  // Handle HTTP errors
                  const errorData = await response.text();
                  throw new Error(`Error: ${errorData}`);
                }
              })
              .catch((error) => {
                // Handle fetch errors
                throw new Error(
                  'Failed to communicate with the API: ' + error.message
                );
              });
          });
        },


        // Créer un nouveau commentaire (conversation principale)
        tinycomments_create: async (req, done, fail) => {
          try {
            const currentTextId = textIdRef.current;
            if (!currentTextId) return fail('Missing textId');
            const { content } = req;
            const { status, data } = await axiosPrivate.post(
              `/texts/external/comment/${currentTextId}`,
              { content }
            );

            if (status !== 200 && status !== 201) {

              throw new Error(`Failed to create comment (HTTP ${status})`);
            }

            const conversationUid =
              String(data?.conversation?.id ?? data?.conversationUid ?? data?.id ?? '');

            if (!conversationUid) {
              throw new Error('Missing conversation id in API response');
            }
            done({ conversationUid });
          } catch (e) {
            console.error('❌ Erreur création:', e);
            fail(e);
          }
        },

        tinycomments_reply: (req, done, fail) => {

          const { conversationUid, content } = req;
          const currentTextId = textIdRef.current;
          if (!currentTextId) return fail('Missing textId');

          const requestBody = {
            content: content,
            conversationUid: conversationUid,
          };

          axiosPrivate.post(`/texts/external/comment/${currentTextId}`, requestBody)
            .then((response) => {
              const data = response.data;
              console.log('✅ Réponse créée:', data);

              done({
                commentUid: data.comment?.id || data.id,
              });
            })
            .catch((error) => {
              console.error('❌ Erreur réponse:', error);
              fail(error);
            });
        },

        // Modifier un commentaire
        tinycomments_edit_comment: (req, done, fail) => {
          const { conversationUid, commentUid, content } = req;

          console.log('✏️ Modification commentaire:', commentUid);

          const requestBody = {
            content: content,
          };

          axiosPrivate.put(`/texts/external/comment/${commentUid}`, requestBody)
            .then((response) => {
              console.log('✅ Commentaire modifié');
              done({
                canEdit: true,
                canDelete: true,
                modifiedAt: new Date().toISOString(),
              });
            })
            .catch((error) => {
              console.error('❌ Erreur modification:', error);
              fail(error);
            });
        },

        // Supprimer une conversation entière
        tinycomments_delete: (req, done, fail) => {
          const { conversationUid } = req;

          console.log('🗑️ Suppression conversation:', conversationUid);

          axiosPrivate.delete(`/texts/external/comment/${conversationUid}`)
            .then((response) => {
              console.log('✅ Conversation supprimée');
              done({
                canDelete: true,
              });
            })
            .catch((error) => {
              console.error('❌ Erreur suppression conversation:', error);
              fail(error);
            });
        },

        // Supprimer tous les commentaires
        tinycomments_delete_all: (req, done, fail) => {
          const { conversationUid } = req;

          const currentTextId = textIdRef.current;
          if (!currentTextId) return fail('Missing textId');
          axiosPrivate.delete(`/texts/external/comment/text/${currentTextId}`)
            .then((response) => {
              console.log('✅ Tous les commentaires supprimés');
              done({
                canDelete: true,
              });
            })
            .catch((error) => {
              console.error('❌ Erreur suppression tous:', error);
              fail(error);
            });
        },

        // Supprimer un commentaire spécifique dans une conversation
        tinycomments_delete_comment: (req, done, fail) => {
          const { conversationUid, commentUid } = req;

          console.log('🗑️ Suppression commentaire:', commentUid, 'de la conversation:', conversationUid);

          axiosPrivate.delete(`/texts/external/comment/${commentUid}`)
            .then((response) => {
              console.log('✅ Commentaire supprimé');
              done({
                canDelete: true,
              });
            })
            .catch((error) => {
              console.error('❌ Erreur suppression commentaire:', error);
              fail(error);
            });
        },

        // Récupérer une conversation spécifique


        tinycomments_lookup: (req, done, fail) => {
          const { conversationUid } = req;

          const toISO = (d) => (d ? new Date(d).toISOString() : new Date().toISOString());

          const flattenThread = (node, acc = []) => {
            if (!node) return acc;
            const c = node.comment ?? node;
            acc.push({
              uid: String(c.id),
              author: String(c.author ?? ''),
              authorName: c.author ?? undefined,
              content: String(c.content ?? ''),
              createdAt: toISO(c.createdAt),
              modifiedAt: toISO(c.modifiedAt ?? c.createdAt),
            });
            const replies = Array.isArray(node.replies) ? node.replies : [];
            for (const r of replies) flattenThread(r, acc);
            return acc;
          };

          // ✅ Attendre 200ms avant de faire le GET
          setTimeout(() => {
            axiosPrivate
              .get(`/texts/external/comment/${conversationUid}`)
              .then(({ data }) => {
                if (!data) {
                  return done({
                    conversation: { uid: String(conversationUid), comments: [] },
                  });
                }

                const root = data.comment ?? data;
                const comments = flattenThread(data)
                  .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

                const safeConvUid = String(
                  conversationUid || root.conversationUid || root.id || ''
                );

                done({
                  conversation: {
                    uid: safeConvUid,
                    comments,
                  },
                });
              })
              .catch((err) => {
                console.error('lookup failed', err);
                fail(err);
              });
          }, 600); // ⏱️ délai en ms
        },



        // Récupérer tous les commentaires
        tinycomments_fetch: (req, done, fail) => {
          try {
            console.log('[tinycomments_fetch] CALLED with req =', req);
            const { conversationUids } = req || {};
            const wanted = new Set((conversationUids || []).map(String));

            const currentTextId = textIdRef.current;
            if (!currentTextId) {
              console.warn('[tinycomments_fetch] Missing textId');
              return done({ conversations: {} });
            }

            axiosPrivate.get(`/texts/external/comment/${currentTextId}`)
              .then(({ data }) => {
                const list = Array.isArray(data) ? data : [];
                const bucket = {};

                list.forEach((item) => {
                  const c = item?.comment ?? item;
                  // fallback: racine => convUid = id si conversationUid null
                  const convUid = String(c.conversationUid ?? c.parentCommentId ?? c.id ?? '');
                  if (!convUid) return;
                  // si Tiny demande un sous-ensemble, filtre
                  if (wanted.size && !wanted.has(convUid)) return;
                  if (!bucket[convUid]) bucket[convUid] = [];
                  bucket[convUid].push(c);
                });

                const toISO = (d) => (d ? new Date(d).toISOString() : new Date().toISOString());
                const conversations = Object.fromEntries(
                  Object.entries(bucket).map(([uid, arr]) => {
                    const comments = arr
                      .map((c) => ({
                        uid: String(c.id),
                        author: String(c.author ?? ''),
                        authorName: c.author ?? undefined,
                        content: String(c.content ?? ''),
                        createdAt: toISO(c.createdAt),
                        modifiedAt: toISO(c.modifiedAt ?? c.createdAt),
                      }))
                      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

                    return [String(uid), { uid: String(uid), comments }];
                  })
                );

                console.log('[tinycomments_fetch] returning conversations:', conversations);
                done({ conversations }); // <-- IMPORTANT: objet avec { conversations }
              })
              .catch((err) => {
                console.error('❌ [tinycomments_fetch] error:', err);
                fail(err);
              });
          } catch (e) {
            console.error('❌ [tinycomments_fetch] catch:', e);
            fail(e);
          }
        },
      }}
    />
  );
};

// Transformer un tableau de commentaires en format conversation TinyMCE
function transformCommentsToConversation(commentsArray, conversationUid) {
  if (!commentsArray || commentsArray.length === 0) {
    return null;
  }

  // Trier les commentaires : parent en premier, puis les réponses
  const sortedComments = commentsArray.sort((a, b) => {
    const aIsParent = !a.comment.parentCommentId || a.comment.parentCommentId === conversationUid;
    const bIsParent = !b.comment.parentCommentId || b.comment.parentCommentId === conversationUid;

    if (aIsParent && !bIsParent) return -1;
    if (!aIsParent && bIsParent) return 1;

    // Sinon trier par date de création
    return new Date(a.comment.createdAt) - new Date(b.comment.createdAt);
  });

  // Transformer en format TinyMCE
  const comments = sortedComments.map(item => ({
    uid: item.comment.id,
    author: item.comment.author || 'Utilisateur',
    authorName: item.comment.author || 'Utilisateur',
    content: item.comment.content,
    createdAt: item.comment.createdAt || new Date().toISOString(),
    modifiedAt: item.comment.modifiedAt,
    canEdit: true,
    canDelete: true,
  }));

  return {
    uid: conversationUid,
    comments: comments,
  };
}
